import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@avds/icons";
import { useSession } from "@/lib/auth";
import { fmtDate, fmtLongDate, fmtMoney } from "@/lib/format";
import { Status } from "@/components/ui/Status";
import { getProformaStats, listProformas } from "@/features/proformas/proformas.api";
import { DEFAULT_LEAD, LeadFilter } from "@/features/proformas/ProjectLead";
import type { Proforma } from "@/lib/types";

export const Route = createFileRoute("/_shell/dashboard")({
  // The figures are those of my work queue unless another scope is picked; only
  // that other scope goes in the URL, so the KPIs carry it into the list.
  validateSearch: (search: Record<string, unknown>): { lead?: string } => ({
    lead: typeof search.lead === "string" && search.lead ? search.lead : undefined,
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const firstName = (session?.displayName ?? "").split(" ")[0] || t("user.unknownUser");
  const today = fmtLongDate(new Date().toISOString());
  const dateLabel = today.charAt(0).toUpperCase() + today.slice(1);
  // By the viewer's own clock — it said "Goedemorgen" all day long.
  const hour = new Date().getHours();
  const greetingKey =
    hour < 12 ? "dashboard.greetingMorning" : hour < 18 ? "dashboard.greetingAfternoon" : "dashboard.greetingEvening";
  const navigate = useNavigate();
  const lead = Route.useSearch().lead ?? DEFAULT_LEAD;
  // Carried into every link to the list, so a KPI opens on the rows it counted.
  const leadSearch = lead === DEFAULT_LEAD ? {} : { lead };

  const statsQ = useQuery({
    queryKey: ["proformas", "stats", lead],
    queryFn: () => getProformaStats(lead),
    placeholderData: keepPreviousData,
  });
  const stats = statsQ.data;
  const attention = (stats?.draft ?? 0) + (stats?.inReview ?? 0) + (stats?.rejected ?? 0);

  const KPIS: { key: string; labelKey: string; value: number; status: string; accent?: string }[] = [
    { key: "draft", labelKey: "dashboard.kpi.toReview", value: stats?.draft ?? 0, status: "draft" },
    { key: "sent", labelKey: "dashboard.kpi.awaitingRef", value: stats?.sent ?? 0, status: "sent" },
    { key: "rejected", labelKey: "dashboard.kpi.rejected", value: stats?.rejected ?? 0, status: "rejected", accent: "var(--danger-600)" },
    { key: "approved", labelKey: "dashboard.kpi.approved", value: stats?.approved ?? 0, status: "approved" },
  ];

  return (
    <div className="avds-page" data-screen-label="Back-office Dashboard">
      <div className="avds-hstack" style={{ justifyContent: "space-between", marginBottom: "var(--space-5)" }}>
        <div>
          <h1 className="t-h2">{t(greetingKey, { name: firstName })}</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "var(--fs-md)", marginTop: "2px" }}>
            {t("dashboard.subtitle", { date: dateLabel, count: attention })}
          </p>
        </div>
        <div className="avds-hstack" style={{ gap: "var(--space-3)" }}>
          <LeadFilter
            value={lead}
            onChange={(v) => void navigate({ to: "/dashboard", search: v === DEFAULT_LEAD ? {} : { lead: v } })}
          />
          <Link to="/proformas/new" className="avds-btn avds-btn--accent">
            <Icon name="plus-circle" size={15} /> {t("proforma.new")}
          </Link>
        </div>
      </div>

      {/* KPIs → deep links into the filtered list */}
      <section className="kpi-grid" style={{ marginBottom: "var(--space-5)" }}>
        {KPIS.map((k) => (
          <Link
            key={k.key}
            to="/proformas"
            search={{ status: k.key, ...leadSearch }}
            className="kpi"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <div className="lbl">{t(k.labelKey as never)}</div>
            <div className="num" style={k.accent ? { color: k.accent } : undefined}>{k.value}</div>
          </Link>
        ))}
      </section>

      {/* Work queues */}
      <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)", alignItems: "start" }}>
        {/* "Wacht op mijn beoordeling" is only true of my own work queue. */}
        <QueueCard
          status="draft"
          lead={lead}
          titleKey={lead === DEFAULT_LEAD ? "dashboard.queue.toReview" : "dashboard.kpi.toReview"}
        />
        <QueueCard status="sent" lead={lead} titleKey="dashboard.queue.awaitingRef" showSent />
      </section>

      {/* Vier-ogen: proformas waiting for a check. In my own queue that is the ones
          I was asked to check, plus mine that a colleague is checking. */}
      {(stats?.inReview ?? 0) > 0 && (
        <section style={{ marginTop: "var(--space-4)" }}>
          <div className="avds-alert avds-alert--info">
            <Icon name="users" size={16} />
            <div>
              {t("dashboard.inReviewAlert", { count: stats?.inReview ?? 0 })}{" "}
              <Link to="/proformas" search={{ status: "in_review", ...leadSearch }} style={{ color: "inherit", textDecoration: "underline" }}>
                {t("dashboard.inReviewLink")}
              </Link>
            </div>
          </div>
        </section>
      )}

      {(stats?.rejected ?? 0) > 0 && (
        <section style={{ marginTop: "var(--space-4)" }}>
          <div className="avds-alert avds-alert--danger">
            <Icon name="alert-circle" size={16} />
            <div>
              {t("dashboard.rejectedAlert", { count: stats?.rejected ?? 0 })}{" "}
              <Link to="/proformas" search={{ status: "rejected", ...leadSearch }} style={{ color: "inherit", textDecoration: "underline" }}>
                {t("dashboard.rejectedLink")}
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function QueueCard({
  status,
  lead,
  titleKey,
  showSent,
}: {
  status: string;
  lead: string;
  titleKey: string;
  showSent?: boolean;
}) {
  const { t } = useTranslation();
  const me = useSession().data?.email.toLowerCase() ?? null;
  const q = useQuery({
    queryKey: ["proformas", { status, lead, page: 1 }],
    queryFn: () => listProformas({ status, lead, pageSize: 6 }),
    placeholderData: keepPreviousData,
  });
  const rows: Proforma[] = q.data?.data ?? [];

  return (
    <div className="avds-card">
      <div className="avds-card__header">
        <div className="avds-card__title">{t(titleKey as never)}</div>
        <Link
          to="/proformas"
          search={{ status, ...(lead === DEFAULT_LEAD ? {} : { lead }) }}
          className="avds-btn avds-btn--ghost avds-btn--sm"
        >
          {t("common.viewAll")} →
        </Link>
      </div>
      <div className="avds-card__body" style={{ paddingBlock: "var(--space-1)" }}>
        {rows.length === 0 ? (
          <p style={{ color: "var(--text-secondary)", fontSize: "var(--fs-sm)", padding: "var(--space-3) 0" }}>
            {t("dashboard.queueEmpty")}
          </p>
        ) : (
          rows.map((p) => (
            <Link
              key={p.id}
              to="/proformas/$id"
              params={{ id: p.number }}
              className="queue-item"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div>
                <div className="q-num">
                  {p.number}
                  {/* ordernr-volgnummer, as in the list — the proforma number alone
                      does not say which TALIS order and activity it is. */}
                  {p.orderRef && (
                    <>
                      {" · "}
                      <span style={{ whiteSpace: "nowrap" }}>{t("proformas.orderRef", { nr: p.orderRef })}</span>
                    </>
                  )}
                </div>
                <div className="q-title">
                  {p.partyName} — {p.title}
                  {/* In my queue but not mine: say whose it is, or that nobody
                      with access leads it — that is why it is here. */}
                  {p.projectLeadEmail !== me && (
                    <span style={{ color: "var(--text-tertiary)" }}>
                      {" · "}
                      {p.projectLeadName ?? p.projectLeadEmail ?? t("lead.unassigned")}
                    </span>
                  )}
                </div>
              </div>
              <div className="avds-hstack" style={{ gap: "var(--space-3)" }}>
                <span className="mono" style={{ fontSize: "var(--fs-sm)", color: "var(--text-secondary)" }}>
                  {/* Excl. VAT, like the list — one amount convention across the
                      back-office, and it matches the tariff lines. */}
                  {showSent ? fmtDate(p.sentToCustomerAt) : fmtMoney(p.subtotalCents, p.currency)}
                </span>
                <Status status={p.status} />
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
